import { Injectable, Inject, PLATFORM_ID, Type, Optional, inject } from '@angular/core';
import { ActivatedRoute, Route, Router, Routes } from '@angular/router';
import { DOCUMENT, isPlatformBrowser, PlatformLocation } from '@angular/common';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { FeatureFlag } from 'dataModels/feature-flag';
import { QuoteFlow } from 'dataModels/quote-flow';
import { Section } from 'dataModels/section';
import { Page } from 'dataModels/page';
import { RouteControllerGuard } from 'route-controller/route-controller.guard';
import { ErrorDialogService } from 'shared/base/generics/globals/errors/error-dialog.service';
import { generateRouteFromUrl } from 'shared/base/generics/helpers/route-helper';
import { onlyUnique } from 'shared/base/generics/helpers/utils';
import {
    SSR_AB_TEST_VARIANTS,
    WebtrendsResponseFactors,
    WebtrendsTestType,
} from 'shared/base/generics/models/webtrends-response-factors.model';
import { AppConfigService } from 'shared/base/generics/services/app-config.service';
import { AppInitService } from 'shared/base/generics/services/app-init.service';
import { TransferStateService } from 'shared/base/generics/services/transfer-state.service';
import { CmsService } from 'sharedStores/cms/cms.service';
import { selectError } from 'sharedStores/error/error.selectors';
import {
    QuoteControllerActions,
    QuoteControllerSelectors,
} from 'sharedStores/quote-controller/quote-controller.module';
import { BlankRouterPageComponent } from 'shared/base/templates/pages/blank-router-page/blank-router-page.component';
import { selectLeadNumber } from 'stores/quote/quote.selectors';
import { QuoteActions, QuoteSelectors } from 'stores/quote/quote.module';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { DropOffPageComponent } from 'templates/pages/drop-off-page/drop-off-page.component';
import { Route as CmsRoute } from 'sharedStores/cms/models/route';
import { QuoteControllerService_Token } from 'shared/base/generics/injection-tokens/dependency-tokens';
import { CmsActions } from 'sharedStores/cms/cms.module';
import { selectDropOffData } from 'stores/personal/personal.selectors';
import { untilDestroyed } from '@ngneat/until-destroy';
import { PAGE_ROUTES, SECTION_ROUTES } from '../constants/routes.constants';
import { staticDataResolver } from '../route-initialiser/static-data.resolver';

/**
 * Quote App Init Service handles quote-specific initialization logic.
 */
@Injectable({
    providedIn: 'root',
})
export class QuoteAppInitService extends AppInitService {
    private route: CmsRoute;
    public quoteControllerService$ = inject(QuoteControllerService_Token);
    public quoteFlowType = this.store$.selectSignal(QuoteSelectors.selectQuoteFlowType);
    public dropOffState$ = this.store$.select(selectDropOffData);

    constructor(
        protected readonly cmsService$: CmsService,
        protected readonly appConfigService$: AppConfigService,
        protected readonly router: Router,
        protected readonly activatedRoute: ActivatedRoute,
        protected readonly platformLocation: PlatformLocation,
        protected readonly store$: Store,
        protected readonly errorDialogService$: ErrorDialogService,
        protected readonly transferStateService: TransferStateService,
        @Inject(PLATFORM_ID) protected readonly platformId: Object,
        @Optional()
        @Inject(SSR_AB_TEST_VARIANTS)
        protected readonly ssrAbTestVariants: WebtrendsResponseFactors,
        private readonly actions$: Actions,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        super(
            appConfigService$,
            router,
            activatedRoute,
            platformLocation,
            store$,
            transferStateService,
            platformId,
        );
    }
    async appInit() {
        this.setSsrAbTestVariants();

        await this.appConfigService$.loadAppConfig();

        const quoteFlow = await this.getQuoteFlow();

        // Determine if we're in the ID-first AB test variant
        const ssrAbTestVariants = (
            isPlatformBrowser(this.platformId)
                ? this.transferStateService.get(SSR_AB_TEST_VARIANTS)
                : this.ssrAbTestVariants
        ) as WebtrendsResponseFactors;

        const isIdFirstVariant = ssrAbTestVariants?.abTestVariants?.['idNumberFirst'] ?? false;

        // AB Test Override: If Id Number first variant is active, set it as entrypoint
        if (isIdFirstVariant) {
            if (quoteFlow?.data) {
                quoteFlow.data.environmentId = this.appConfigService$.config().cmsEnvId;
                quoteFlow.data.abTestingId = 'id-number-first';
                quoteFlow.data.baseRoute = 'policy';
            }
            quoteFlow?.data?.sections?.forEach((section: Section) => {
                if (section.route === SECTION_ROUTES.PERSONAL_DETAILS) {
                    section.pages?.forEach((page: Page) => {
                        if (page.route === PAGE_ROUTES.ID_NUMBER) {
                            page.flowEntryPoint = true;
                        }
                    });
                }
            });
            this.store$.dispatch(CmsActions.getFlowResponse({ data: quoteFlow }));
        }

        this.setupRoutes(
            quoteFlow?.data?.sections.map((el: Section) => el.baseRoute).filter(onlyUnique),
            quoteFlow?.data?.featureFlags,
        );

        await this.initQuoteFlow(quoteFlow);
    }

    /**
     * This enables us to use the base route setup in squidex to configure the base-route of the application
     * NOTE: Each brand has brand specific override
     * @param baseRoutes
     * @param featureFlags
     * @private
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public setupRoutes(baseRoutes: Array<string>, featureFlags: FeatureFlag) {
        if (baseRoutes) {
            const routes: Routes = [];
            this.router.resetConfig(routes);
        }
    }

    /**
     * This sets the SSR_AB_TEST_VARIANTS in the store or transfer state depending on if we are in the browser or server
     * @private
     */
    private setSsrAbTestVariants() {
        if (isPlatformBrowser(this.platformId)) {
            // dispatch the setSsrAbTestVariants action to set the store
            const ssrAbTestVariants = this.transferStateService.get(SSR_AB_TEST_VARIANTS);
            this.store$.dispatch(
                QuoteControllerActions.setSsrAbTestVariants({ ssrAbTestVariants }),
            );
        } else {
            // use transferStateService to set the SSR_AB_TEST_VARIANTS and transfer it to the client
            this.transferStateService.set(SSR_AB_TEST_VARIANTS, this.ssrAbTestVariants);
        }
    }

    /**
     * The initQuoteFlow method is responsible for initializing the quote flow by checking if the quote flow is
     * available, and if so, navigating to the correct route depending on if we have a leadnumber or not
     * @param quoteFlow
     * @private
     */
    private async initQuoteFlow(quoteFlow: QuoteFlow): Promise<void> {
        const page = this.store$.selectSignal(
            QuoteControllerSelectors.selectPageByRoute({ route: this.route }),
        )();
        const leadNumber = this.store$.selectSignal(selectLeadNumber)();
        const isCallback = this.platformLocation.href.includes('callback');
        if (isCallback) {
            this.dropOffState$.pipe(take(1), untilDestroyed(this)).subscribe((state) => {
                if (state?.dropOffState?.code) {
                    this.router.navigate(['callback'], { queryParamsHandling: 'preserve' });
                }
            });

            return;
        }

        // If the external site has redirected a user with pre-filled details, treat it as a valid
        // direct-entry point. Do NOT reset to the start of the flow; instead navigate to contact-details
        // which will auto-submit and proceed to the intended page without showing the form.
        const queryParams = this.route?.queryParams ?? {};
        const { firstName, lastName, contactNumber, dateTime } = queryParams;

        const hasExternalParams = firstName && lastName && contactNumber;

        // Any marketing param present in the URL signals a marketing redirect attempt.
        // Used to catch partial/malformed links (e.g. missing firstName) that would otherwise
        // slip past the hasExternalParams guard and fall through to getStartingRoute() -> '/'.
        const hasAnyExternalParam = !!(firstName || lastName || contactNumber || dateTime);

        // Reject stale links — marketing redirects are only valid within 1 minute.
        const isDateTimeStale = (() => {
            if (!dateTime) return false;
            const parsedTime = new Date(dateTime);
            if (isNaN(parsedTime.getTime())) return true;
            return Date.now() - parsedTime.getTime() > 60 * 1000;
        })();

        // Validate SA cell number format (mirrors contact-details-page validation).
        const cellphoneValue = contactNumber?.replace(/\s/g, '');
        const isValidCellphone =
            cellphoneValue &&
            /^(0|27)[678]\d{8}$/.test(cellphoneValue) &&
            cellphoneValue.length === 10;

        // All three conditions must hold: params present, link is fresh, phone is valid.
        // If any fail, treat as no external params and redirect to the start of the flow.
        const hasValidExternalParams = hasExternalParams && !isDateTimeStale && isValidCellphone;

        // Params were supplied by marketing but are stale or invalid — hard-redirect to the base
        // route (/car-insurance or /home-contents). We use window.location.replace rather than
        // router.navigate because this runs inside APP_INITIALIZER: Angular's initial navigation
        // hasn't fired yet, so a programmatic router call would be overridden when the initialiser
        // resolves. A hard redirect also clears the invalid query params from the URL cleanly.
        if (hasAnyExternalParam && !hasValidExternalParams && isPlatformBrowser(this.platformId)) {
            this.document.location.replace('/' + this.route.baseRoute);
            return;
        }

        if (quoteFlow) {
            // check if route starts with car to check for old deprecated route
            const baseRouteIsOldRoute = this.route?.baseRoute === 'car';

            // check if route starts with car-insurance/resume-quote to check for old deprecated resume-quote route
            const baseResumeRouteIsOldRoute =
                this.route?.baseRoute === 'car-insurance' &&
                this.route?.sectionRoute === 'resume-quote';

            // If page is not flowEntryPoint, and we don't have a lead number,
            // then we need to navigate to the starting point, else simply navigate to the route given
            // we only want to navigate to the starting point if we are in the browser as the server will never have
            // the lead number available
            if (
                (isPlatformBrowser(this.platformId) &&
                    !page?.page?.flowEntryPoint &&
                    !leadNumber &&
                    !hasValidExternalParams) ||
                baseRouteIsOldRoute
            ) {
                this.route = this.quoteControllerService$.getStartingRoute();
                this.quoteControllerService$.navigate(this.route);
            } else if (baseResumeRouteIsOldRoute) {
                this.route.baseRoute = 'policy';
                this.quoteControllerService$.navigate(this.route);
            } else {
                // When redirected from marketing with pre-filled params, proactively create a lead
                // and await its completion before navigating. Without this, createLead and
                // addUpdateVehicle race each other on the first car make click, causing a 401.
                if (hasValidExternalParams && !leadNumber && isPlatformBrowser(this.platformId)) {
                    // Set the quote flow type from the baseRoute so the trackbar can match tracks
                    // correctly. Without this, quoteFlowType stays null (it is normally set on the
                    // landing page) and getCurrentTrack never finds a match, leaving tracks empty.
                    this.store$.dispatch(
                        QuoteActions.setQuoteFlowType({
                            quoteFlowType: this.route.baseRoute as QuoteFlowType,
                        }),
                    );
                    this.store$.dispatch(QuoteActions.createLeadRequest());
                    await firstValueFrom(
                        this.actions$.pipe(ofType(QuoteActions.createLeadResponse), take(1)),
                    );

                    // Strip marketing params from the route before navigating so they never
                    // appear in the final URL. Must be done here (not via router.navigate after
                    // the fact) because we are still inside APP_INITIALIZER.
                    const {
                        firstName: _fn,
                        lastName: _ln,
                        contactNumber: _cn,
                        dateTime: _dt,
                        ...remainingParams
                    } = this.route.queryParams ?? {};
                    this.route = { ...this.route, queryParams: remainingParams };
                }
                this.quoteControllerService$.navigate(this.route);
            }
        } else {
            throw Error(`Unable to Init Quote flow - Missing QuoteFlow`);
        }
    }

    /**
     * Get Quote flow called when we initialize the app - this forces Angular Universal to wait for this call to
     * complete before rendering the page
     * @private
     */
    private async getQuoteFlow() {
        const url = this.platformLocation.href;
        this.route = generateRouteFromUrl(url, this.platformId);
        let abTestId = this.route?.queryParams?.['abid'] || '';

        const ssrAbTestVariants: WebtrendsResponseFactors =
            this.transferStateService.get(SSR_AB_TEST_VARIANTS);

        // If this is a variant, use the testId
        if (
            this.ssrAbTestVariants?.squidexAbTestType === WebtrendsTestType.VARIANT ||
            ssrAbTestVariants?.squidexAbTestType === WebtrendsTestType.VARIANT
        ) {
            abTestId =
                this.ssrAbTestVariants?.squidexAbTestId || ssrAbTestVariants?.squidexAbTestId;
        }

        // This allows us to keep car-insurance as the default starting route for the brands that don't yet have home contents
        if (!this.route?.baseRoute) {
            this.route.baseRoute = this.appConfig().defaultBaseRoute;
        }

        // Set query parameters in QuoteControllerService
        this.quoteControllerService$.setQueryParams(this.route.queryParams);

        // Override the home contents flow for skip contactDetailsPage
        // Note: Derive quoteFlowType from route.baseRoute (already available) instead of signal
        // which hasn't been populated yet. This ensures SSR and browser consistency.
        const isSkipContactDetailsVariant =
            ssrAbTestVariants?.abTestVariants?.['skipContactDetailsPage'] ?? false;
        const routeIsHomeContents =
            (this.quoteFlowType() == QuoteFlowType.HomeContents &&
                this.route?.baseRoute === 'policy') ||
            this.route?.baseRoute === QuoteFlowType.HomeContents ||
            this.route?.baseRoute === 'home-contents';

        if (isSkipContactDetailsVariant && routeIsHomeContents) {
            abTestId = '';
        }

        try {
            return await firstValueFrom(
                this.cmsService$.getQuoteSetup({
                    abTestId,
                    baseRoute: this.route.baseRoute,
                    route: this.route.sectionRoute,
                }),
            );
        } catch (e) {
            console.error(e);
            throw new Error(
                'Unable to fetch quote flow, check that your API is running and if you are local ensure NODE_TLS_REJECT_UNAUTHORIZED=0 is set',
            );
        }
    }

    public setupRoutesBase(
        featureFlags: FeatureFlag,
        baseRoutes: Array<string>,
        basePageComponent: Type<any>,
        dynamicPageComponent: Type<any>,
    ) {
        const childRoutes: Routes = [];

        if (this.appConfig().defaultBaseRoute != '') {
            // If Home Contents is not active, ensure we default to car-insurance
            const redirectRoute: Route = {
                path: '',
                redirectTo: this.appConfig().defaultBaseRoute,
                pathMatch: 'full',
            };
            childRoutes.push(redirectRoute);
        }

        for (const baseRoute of baseRoutes) {
            if (this.routeIsActive(baseRoute, featureFlags)) {
                const route: Route = {
                    path: baseRoute,
                    component: BlankRouterPageComponent,
                    children: [
                        {
                            path: '**',
                            canActivate: [RouteControllerGuard],
                            component: dynamicPageComponent,
                            resolve: { data: staticDataResolver },
                        },
                    ],
                };
                childRoutes.push(route);
            }
        }

        const routes: Routes = [
            {
                path: 'car',
                pathMatch: 'prefix',
                redirectTo: this.appConfig().defaultBaseRoute,
            },
            {
                path: '',
                component: basePageComponent,
                children: [
                    {
                        path: 'callback',
                        component: DropOffPageComponent,
                    },
                    ...childRoutes,
                ],
            },
        ];

        this.router.resetConfig(routes);
    }

    routeIsActive(baseRoute: string, featureFlags: FeatureFlag) {
        return !(baseRoute == 'home-contents' && !featureFlags.hasHomeContents);
    }

    private checkErrorState() {
        // If the app is in error state (and in production mode) always ensure we show the error dialog to
        // ensure the user is not able to continue with the app in an invalid state
        const errorState = this.store$.selectSignal(selectError)();
        if (errorState != null) {
            this.errorDialogService$.openDialog(
                this.appConfig()?.globalErrorDialogTitle,
                errorState.message,
                errorState.status,
            );
        }
    }
}
